/**
 * QuickMenu config — presentation order + keybinding-action map for the
 * player-facing quick menu (bottom-right ≡).
 *
 * The menu DERIVES its entries from the window registry's `game` category
 * (single source of truth for ids/labels/icons/descriptions) — this file only
 * decides the player-friendly ORDER and which keybinding ACTION each window
 * mirrors, so the hotkey hints on the buttons are always the LIVE bindings
 * from KeyBindingManager (the old MicroMenu hardcoded tooltip keys that were
 * never actually bound). Rebuilt 2026-06-12 (DJ: "newer sleeker player-facing
 * micro menu… all should be bindable; user configurability is best").
 */

import type { WindowId } from '../../game/ui/framework/types';

/** Player-friendly render order. Game-category windows NOT listed here still
 *  render (appended in registry order) — a new game window can never be
 *  silently missing from the menu. */
export const QUICK_MENU_ORDER: readonly WindowId[] = [
  'inventory',
  'equipment',
  'spells',
  'quests',
  'worldMap',
  'leaderboard',
  'speedrunRecords',
  'jobs',
  'talents',
  'party',
  'trade',
  'bank',
  'crafting',
  'lobbyTools',
  'keyBindings',
  'settings',
];

/** Registered windows temporarily withheld from player navigation. Keeping the
 * registry entry preserves the dormant implementation for a future re-enable. */
export const QUICK_MENU_HIDDEN: ReadonlySet<WindowId> = new Set<WindowId>([
  'twitchPiP',
  // Inspect needs a target: it opens from the target frame, not the menu (lane BM).
  'inspect',
]);

/** WindowId → KeyBindingManager action whose live key renders as the hint.
 *  Windows without an entry simply show no hotkey hint. */
export const QUICK_MENU_BINDING_ACTION: Partial<Record<WindowId, string>> = {
  inventory: 'inventory',
  equipment: 'character_sheet',
  talents: 'talents',
  spells: 'skills',
  quests: 'quest_log',
  worldMap: 'world_map',
  leaderboard: 'leaderboard',
  party: 'party',
  trade: 'trade',
  bank: 'bank',
  crafting: 'crafting',
  keyBindings: 'key_bindings',
};

/** Windows that only render on lobby maps (gate carried over from the old
 *  MicroMenu's lobbyOnly filter — LobbyToolsPanel does not self-gate). */
export const QUICK_MENU_LOBBY_ONLY: ReadonlySet<WindowId> = new Set<WindowId>(['lobbyTools']);
